import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { forceSimulation, forceX, forceY, forceCollide } from "d3-force"

import { loadTexts, sanitize } from "../../utils/i18n"
import emptySentimentImage from "../../images/empty/sentiment-analysis.svg"
import "./index.less"

// Paleta definida en Figma (VLENG-11139): 🟢 Sentiment Analysis, node 149-2479.
// Usada para la leyenda y el tooltip — NO para el relleno de la burbuja.
export const COLOR_POSITIVE = "#55C8AE"
export const COLOR_NEUTRAL = "#A7A3A3"
export const COLOR_NEGATIVE = "#E37879"
const COLOR_AXIS = "#d0d0d0"

// Escala de gradiente continuo definida en Figma (VLENG-11399, node 280-1938).
// Distinta de la paleta plana de arriba: acá el relleno de cada burbuja
// interpola entre estos stops según RN-04/RN-25. El neutral (#E9E9E9) es
// EXCLUSIVO de score=0 — no es un stop más del blend: si estuviera adentro
// del array, una burbuja apenas negativa mezclaría con gris y recién se
// vería #F9E4E4 puro a partir de -15 de score. #F9E4E4/#D5F0EA son el piso
// (mínimo) real de cada lado, ya con score=~0.
const GRADIENT_NEUTRAL = "#E9E9E9"
const POSITIVE_GRADIENT_STOPS = ["#D5F0EA", "#92E1CF", "#55C8AE", "#3CB99C"]
const NEGATIVE_GRADIENT_STOPS = ["#F9E4E4", "#ECA1A2", "#E37879", "#E06769"]

const DEFAULT_WIDTH = 900
const DEFAULT_HEIGHT = 320
const MIN_RADIUS_FOR_LABEL = 24
const MIN_RADIUS = 14
const MAX_RADIUS = 60
const SIMULATION_TICKS = 300
const AXIS_X_BOTTOM_OFFSET = 4 // pegado al borde inferior, arriba de los labels de la leyenda

const LABEL_FONT_SIZE = 13
const LABEL_CHAR_WIDTH_FACTOR = 0.6 // ancho promedio de un caracter, en fracción del font-size
const LABEL_PADDING = 8 // espacio entre el texto y el borde de la burbuja

const CENTER_BASE_OFFSET = 24 // offset mínimo del centro, incluso para score = 0
const BASE_OFFSET_DAMPING = 0.4 // fracción del alcance máximo que puede sumar el score absoluto propio de la burbuja menos extrema de cada lado (amortiguado con sqrt)

function hexToRgb(hex) {
  const value = hex.replace("#", "")
  return {
    r: parseInt(value.substring(0, 2), 16),
    g: parseInt(value.substring(2, 4), 16),
    b: parseInt(value.substring(4, 6), 16),
  }
}

function mixColor(colorA, colorB, ratio) {
  const a = hexToRgb(colorA)
  const b = hexToRgb(colorB)
  const r = Math.round(a.r + (b.r - a.r) * ratio)
  const g = Math.round(a.g + (b.g - a.g) * ratio)
  const bch = Math.round(a.b + (b.b - a.b) * ratio)
  return `rgb(${r}, ${g}, ${bch})`
}

// Interpola sobre N stops (no solo 2): ratio 0 devuelve el primer stop,
// ratio 1 el último, repartiendo los tramos intermedios en partes iguales.
function mixColorScale(stops, ratio) {
  const clamped = Math.min(Math.max(ratio, 0), 1)
  const segments = stops.length - 1
  const scaled = clamped * segments
  const index = Math.min(Math.floor(scaled), segments - 1)
  return mixColor(stops[index], stops[index + 1], scaled - index)
}

// score > 0 tira a positivo, score < 0 tira a negativo.
function sentimentScore({ positive, negative }) {
  return positive - negative // -100..100
}

// RN-04/RN-25: el color de cada burbuja es un gradiente continuo ponderado
// por su distribución de sentimientos, usando la escala exacta de Figma
// (POSITIVE_GRADIENT_STOPS/NEGATIVE_GRADIENT_STOPS) — no un mix de 2 colores.
// score===0 es el único caso que pinta el neutral; cualquier score !=0 ya
// arranca en el piso del lado correspondiente (#F9E4E4 o #D5F0EA).
function sentimentColor(topic) {
  const score = sentimentScore(topic)
  if (score === 0) return GRADIENT_NEUTRAL
  if (score > 0) {
    return mixColorScale(POSITIVE_GRADIENT_STOPS, Math.min(score / 60, 1))
  }
  return mixColorScale(NEGATIVE_GRADIENT_STOPS, Math.min(-score / 60, 1))
}

// Recorta el nombre del tópico si no entra con un margen razonable dentro del
// círculo, dejando puntos suspensivos en vez de invadir el borde de la burbuja.
// La burbuja NUNCA debe mostrar solo "…": como mínimo se ve un extracto de al
// menos una letra de la palabra seguido de los puntos suspensivos.
function truncateLabel(name, radius) {
  const availableWidth = 2 * (radius - LABEL_PADDING)
  const charWidth = LABEL_FONT_SIZE * LABEL_CHAR_WIDTH_FACTOR
  const maxChars = Math.floor(availableWidth / charWidth)

  if (maxChars >= name.length) return name
  const visibleChars = Math.max(1, maxChars - 1)
  return `${name.slice(0, visibleChars)}…`
}

// El endpoint de sentiment-wordcloud devuelve conteos crudos
// (repetitions/*_count) — se convierten a porcentajes acá, una única vez,
// para que el resto del chart trabaje siempre con porcentajes 0-100.
export function wordsToTopics(words) {
  return (words || []).map((w) => {
    const total = w.repetitions || 0
    const pct = (count) => (total > 0 ? Math.round((count / total) * 100) : 0)
    return {
      name: w.word,
      total,
      positive: pct(w.positive_count),
      neutral: pct(w.neutral_count),
      negative: pct(w.negative_count),
    }
  })
}

// Ordena las burbujas de un mismo lado (todas negativas o todas positivas) de
// menos a más extremas, y las aleja del centro de forma ACUMULATIVA según la
// diferencia de score respecto a la burbuja anterior. La burbuja menos
// extrema de cada lado (índice 0) no usa un offset fijo: parte de
// CENTER_BASE_OFFSET y le suma un plus amortiguado (sqrt) según SU PROPIO
// score absoluto — así una burbuja única 100% negativa se aleja más del
// centro que una única apenas negativa, pero ninguna llega al extremo (eso
// queda reservado para cuando hay varias burbujas que se van acumulando).
function assignSideOffsets(sideTopics, scale, maxReach) {
  const sorted = [...sideTopics].sort(
    (a, b) => Math.abs(a.score) - Math.abs(b.score)
  )

  let cumulative = 0
  let previousAbsScore = 0
  return sorted.map((topic, index) => {
    const absScore = Math.abs(topic.score)
    cumulative =
      index === 0
        ? CENTER_BASE_OFFSET +
          Math.sqrt(absScore / 100) * BASE_OFFSET_DAMPING * maxReach
        : cumulative + scale * (absScore - previousAbsScore)
    previousAbsScore = absScore
    return { ...topic, centerOffset: cumulative }
  })
}

// La posición X de cada burbuja representa su score de sentimiento (negativo
// a la izquierda, positivo a la derecha, neutral = centro del diagrama),
// calculada en relación al resto de burbujas de su mismo lado (ver
// assignSideOffsets). La posición Y no tiene significado — se deja libre
// para que la simulación de fuerzas separe las burbujas y evite overlaps.
function buildBubbleLayout(topics, width, height) {
  if (!topics.length) return []

  // Alto real disponible para las burbujas: no deben cruzar la línea del eje
  // X, que se dibuja AXIS_X_BOTTOM_OFFSET px arriba del borde inferior.
  const drawableHeight = height - AXIS_X_BOTTOM_OFFSET

  const maxTotal = Math.max(...topics.map((t) => t.total))
  const center = width / 2
  const maxReach = center - CENTER_BASE_OFFSET // alcance máximo posible desde el centro
  const scale = maxReach / 100 // px por punto de score

  const withScore = topics.map((topic) => ({
    ...topic,
    score: sentimentScore(topic),
  }))

  const negativeSide = assignSideOffsets(
    withScore.filter((t) => t.score < 0),
    scale,
    maxReach
  )
  const positiveSide = assignSideOffsets(
    withScore.filter((t) => t.score >= 0),
    scale,
    maxReach
  )

  const nodes = [
    ...negativeSide.map((t) => ({ ...t, targetX: center - t.centerOffset })),
    ...positiveSide.map((t) => ({ ...t, targetX: center + t.centerOffset })),
  ].map((topic) => ({
    ...topic,
    r: Math.max(MIN_RADIUS, Math.sqrt(topic.total / maxTotal) * MAX_RADIUS),
  }))

  const simulation = forceSimulation(nodes)
    .force("x", forceX((d) => d.targetX).strength(0.9))
    .force("y", forceY(drawableHeight / 2).strength(0.06))
    .force(
      "collide",
      forceCollide((d) => d.r + 3).iterations(4)
    )
    .stop()

  for (let i = 0; i < SIMULATION_TICKS; i += 1) {
    simulation.tick()
  }

  nodes.forEach((node) => {
    node.x = Math.min(width - node.r, Math.max(node.r, node.x))
    node.y = Math.min(drawableHeight - node.r, Math.max(node.r, node.y))
  })

  return nodes
}

// Chart puramente presentacional: recibe topics ya en porcentajes
// (ver wordsToTopics) y dibuja el circle-pack por sentimiento + leyenda.
// No sabe nada de redux/fetch — lo comparten WidgetQualitativeBubbles
// (ReportsV2) y la vista interna de detalle (containers/Reports/QualitativeQuestion).
export function QualitativeSentimentBubbles(props) {
  const { topics, height } = props
  const [hoveredNode, setHoveredNode] = useState(null)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const canvasRef = useRef(null)
  const intl = loadTexts("reports_qualitative_questions_view")

  useEffect(() => {
    const measure = () => {
      if (canvasRef.current) {
        setWidth(canvasRef.current.clientWidth)
      }
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  const nodes = useMemo(() => buildBubbleLayout(topics, width, height), [
    topics,
    width,
    height,
  ])

  const renderPopup = () => {
    if (!hoveredNode) return null
    const { name, total, positive, neutral, negative } = hoveredNode
    const left = (hoveredNode.x / width) * 100
    const top = ((hoveredNode.y - hoveredNode.r) / height) * 100

    return (
      <div
        className="QualitativeSentimentBubbles-popup"
        style={{ left: `${left}%`, top: `${top}%` }}>
        <div className="QualitativeSentimentBubbles-popup-header">
          <b>{name}</b>
          <span>{`${total} ${intl.answers}`}</span>
        </div>
        <div className="QualitativeSentimentBubbles-popup-bar">
          {positive > 0 && (
            <span style={{ width: `${positive}%`, background: COLOR_POSITIVE }}>
              {`${positive}%`}
            </span>
          )}
          {neutral > 0 && (
            <span style={{ width: `${neutral}%`, background: COLOR_NEUTRAL }}>
              {`${neutral}%`}
            </span>
          )}
          {negative > 0 && (
            <span style={{ width: `${negative}%`, background: COLOR_NEGATIVE }}>
              {`${negative}%`}
            </span>
          )}
        </div>
        <ul className="QualitativeSentimentBubbles-popup-list">
          <li>
            <i style={{ background: COLOR_NEUTRAL }} />
            {`${intl.sentimentNeutral}: ${Math.round((total * neutral) / 100)} ${intl.answers}`}
          </li>
          <li>
            <i style={{ background: COLOR_POSITIVE }} />
            {`${intl.sentimentPositive}: ${Math.round((total * positive) / 100)} ${intl.answers}`}
          </li>
          <li>
            <i style={{ background: COLOR_NEGATIVE }} />
            {`${intl.sentimentNegative}: ${Math.round((total * negative) / 100)} ${intl.answers}`}
          </li>
        </ul>
      </div>
    )
  }

  // RN-19 (precedencia FE-02): superado el umbral y el anonimato, si ninguna
  // palabra cumple los criterios de aparición (RN-22) hay que mostrar el
  // empty state "sin burbujas" en vez de dejar el contenedor en blanco.
  if (nodes.length === 0) {
    const intlEmptyWordCloud = loadTexts("reports_qualitative_questions_view")
      .emptyWordCloud
    return (
      <div className="QualitativeSentimentBubbles-empty" style={{ height }}>
        <img src={emptySentimentImage} alt="" />
        <p>{sanitize(intlEmptyWordCloud)}</p>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="QualitativeSentimentBubbles-canvas" ref={canvasRef}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          style={{ display: "block" }}>
          <line
            className="QualitativeSentimentBubbles-axis QualitativeSentimentBubbles-axis-x"
            x1={0}
            x2={width}
            y1={height - AXIS_X_BOTTOM_OFFSET}
            y2={height - AXIS_X_BOTTOM_OFFSET}
            stroke={COLOR_AXIS}
          />
          <line
            className="QualitativeSentimentBubbles-axis QualitativeSentimentBubbles-axis-y"
            x1={width / 2}
            x2={width / 2}
            y1={0}
            y2={height - AXIS_X_BOTTOM_OFFSET}
            stroke={COLOR_AXIS}
          />
          {nodes.map((node) => (
            <g
              key={node.name}
              transform={`translate(${node.x}, ${node.y})`}
              onMouseEnter={() => setHoveredNode(node)}
              onMouseLeave={() => setHoveredNode(null)}>
              <circle
                r={node.r}
                fill={sentimentColor(node)}
                opacity={hoveredNode && hoveredNode !== node ? 0.35 : 1}
              />
              <text
                textAnchor="middle"
                dy="-2"
                className="QualitativeSentimentBubbles-label-name">
                {truncateLabel(node.name, node.r)}
              </text>
              {node.r >= MIN_RADIUS_FOR_LABEL && (
                <text
                  textAnchor="middle"
                  dy="14"
                  className="QualitativeSentimentBubbles-label-total">
                  ({node.total})
                </text>
              )}
            </g>
          ))}
        </svg>
        {renderPopup()}
      </div>

      <div className="QualitativeSentimentBubbles-legend">
        <span style={{ color: COLOR_NEGATIVE }}>
          <i style={{ background: COLOR_NEGATIVE }} />
          {intl.sentimentNegative}
        </span>
        <span style={{ color: COLOR_NEUTRAL }}>
          <i style={{ background: COLOR_NEUTRAL }} />
          {intl.sentimentNeutral}
        </span>
        <span style={{ color: COLOR_POSITIVE }}>
          <i style={{ background: COLOR_POSITIVE }} />
          {intl.sentimentPositive}
        </span>
      </div>
    </React.Fragment>
  )
}

QualitativeSentimentBubbles.propTypes = {
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      total: PropTypes.number,
      positive: PropTypes.number,
      neutral: PropTypes.number,
      negative: PropTypes.number,
    })
  ),
  height: PropTypes.number,
}

QualitativeSentimentBubbles.defaultProps = {
  topics: [],
  height: DEFAULT_HEIGHT,
}

export default QualitativeSentimentBubbles
